package com.crowpay

import com.lightningkite.UUID
import com.lightningkite.serialization.ClientModule
import com.lightningkite.serialization.serializerOrContextual
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.properties.Properties

expect fun encodeURIComponent(content: String): String

expect fun decodeURIComponent(content: String): String

enum class JumpSection(val requiresId: Boolean = true) {
    ProjectDetails(false),
    ToDos(false),
    ProjectMessages(false),
    LineItemCompletionFlow,
    PendingChangeOrder,
    ChangeItem,
    LineItem,
    ScopeView,
    PayApp,
    ContractorNote,
    WorkTask,
    PunchTask
}


@OptIn(ExperimentalSerializationApi::class)
object URLs {
    init {
        prepareModelsShared()
    }

    @Serializable
    private data class Wrapper<T>(val value: T)

    fun <T> Properties.encodeToStringMap(
        serializer: KSerializer<T>,
        value: T,
        key: String,
    ): Map<String, String> {
        if (value == null) return emptyMap()
        return encodeToStringMap(Wrapper.serializer(serializer), Wrapper(value)).mapKeys {
            it.key.replaceFirst(
                "value",
                key
            )
        }
    }

    val properties = Properties(ClientModule)

    data class UrlLikePath(
        val scheme: String,
        val segments: List<String>,
        val parameters: Map<String, String>
    ) {
        companion object {
            val EMPTY = UrlLikePath("https", listOf(), mapOf())

            fun fromParts(scheme: String, pathname: String, search: String) = UrlLikePath(
                scheme = scheme,
                segments = pathname.split('/').filter { it.isNotBlank() },
                parameters = search.trimStart('?').split('&').filter { it.isNotBlank() }
                    .associate { it.substringBefore('=') to decodeURIComponent(it.substringAfter('=')) }
            )

            fun fromUrlString(url: String): UrlLikePath {
                val parts = url.split("?")
                val beforeParams = parts.getOrNull(0) ?: ""

                return fromParts(beforeParams.substringBefore("://", "https"), beforeParams.substringAfter("://"), parts.getOrNull(1) ?: "")
            }
        }

        fun render() = scheme + "://" + segments.joinToString("/") + (parameters.takeUnless { it.isEmpty() }?.entries?.joinToString(
            "&",
            "?"
        ) { "${it.key}=${encodeURIComponent(it.value)}" } ?: "")

        class ParametersBuilder(start: Map<String, String>) {
            val parameters = start.toMutableMap()

            fun <T> put(key: String, value: T, serializer: KSerializer<T>) {
                parameters.putAll(properties.encodeToStringMap(serializer, value, key))
            }

            inline fun <reified T> put(key: String, value: T) = put(key, value, serializerOrContextual<T>())
        }

        fun withParameters(setup: ParametersBuilder.()->Unit) = copy(parameters = ParametersBuilder(parameters).apply(setup).parameters)

        operator fun plus(other: UrlLikePath) = UrlLikePath(scheme, segments + other.segments, parameters + other.parameters)

        operator fun plus(path: String): UrlLikePath = plus(fromUrlString(path))
    }

    @Serializable
    data class JumpTo(
        val section: JumpSection,
        val id: UUID? = null
    ) {
        init {
            if (section.requiresId && id == null) throw IllegalArgumentException("JumpTo.$section required an ID")
        }

        companion object {
            const val KEY: String = "jumpto"
        }
    }

    val BaseProjectPath = UrlLikePath.fromUrlString("/projects")
}

fun URLs.UrlLikePath.ParametersBuilder.jumpTo(section: JumpSection, id: UUID?) {
    put(URLs.JumpTo.KEY, URLs.JumpTo(section, id), URLs.JumpTo.serializer())
}

fun URLs.UrlLikePath.jumpTo(section: JumpSection, id: UUID?) = withParameters { jumpTo(section, id) }